import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Upload, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { useUploadStore } from '@/store/uploadStore';
import { uploadManager } from '@/upload/uploadManager';
import { DropZone } from '@/components/upload/DropZone';
import { UploadProgressSummary } from '@/components/upload/UploadProgressSummary';
import { UploadQueue } from '@/components/upload/UploadQueue';

export function UploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{ id?: string }>();

  // eventId from route param (/events/:id/upload) or query string (?eventId=...)
  const eventId = params.id ?? searchParams.get('eventId') ?? searchParams.get('event') ?? '';

  const status = useUploadStore((s) => s.status);
  const totalFiles = useUploadStore((s) => s.totalFiles);
  const reset = useUploadStore((s) => s.reset);

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);

  const isIdle = status === 'idle';
  const isActive = status === 'uploading' || status === 'paused';
  const isComplete = status === 'complete';

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleFilesAccepted = useCallback(
    (files: File[]) => {
      setStagedFiles((prev) => [...prev, ...files]);
    },
    [],
  );

  const handleStartUpload = useCallback(async () => {
    if (!eventId) {
      toast.error(t('upload.noEventSelected'));
      return;
    }
    if (stagedFiles.length === 0) {
      toast.error(t('upload.noFiles'));
      return;
    }

    try {
      await uploadManager.start(eventId, stagedFiles);
      setStagedFiles([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start upload';
      toast.error(msg);
    }
  }, [eventId, stagedFiles, t]);

  const handleNewUpload = useCallback(() => {
    reset();
    setStagedFiles([]);
  }, [reset]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {eventId && (
            <button
              type="button"
              onClick={() => navigate(`/events/${eventId}`)}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <h1 className="page-title">{t('nav.upload')}</h1>
        </div>

        {isComplete && (
          <button
            type="button"
            onClick={handleNewUpload}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Upload className="h-4 w-4" />
            {t('upload.newUpload')}
          </button>
        )}
      </div>

      {/* No event selected warning */}
      {!eventId && (
        <div className="mt-4 rounded-md bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          {t('upload.noEventSelected')}
        </div>
      )}

      <div className="mt-6 space-y-5">
        {/* Drop zone -- shown when idle or adding more files */}
        {(isIdle || stagedFiles.length > 0) && !isActive && !isComplete && (
          <>
            <DropZone
              onFilesAccepted={handleFilesAccepted}
              disabled={!eventId}
            />

            {/* Staged files summary + start button */}
            {stagedFiles.length > 0 && (
              <div className="flex items-center justify-between rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800/60">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('upload.stagedSummary', {
                    count: stagedFiles.length,
                    size: formatBytes(
                      stagedFiles.reduce((s, f) => s + f.size, 0),
                    ),
                  })}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStagedFiles([])}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {t('upload.clearAll')}
                  </button>
                  <button
                    type="button"
                    onClick={handleStartUpload}
                    className="btn-primary inline-flex items-center gap-2 text-sm"
                  >
                    <Upload className="h-4 w-4" />
                    {t('upload.startUpload')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Progress summary -- shown when uploading or complete */}
        {(isActive || isComplete) && <UploadProgressSummary />}

        {/* Upload queue -- shown whenever there are items */}
        {totalFiles > 0 && <UploadQueue />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 MB';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
